import type { ReactElement } from 'react'
import { HomePage } from './pages/HomePage'
import { SolutionsPage } from './pages/SolutionsPage'
import { MethodologyPage } from './pages/MethodologyPage'
import { PricingPage } from './pages/PricingPage'
import { ResourcesPage } from './pages/ResourcesPage'
import { LegalPage } from './pages/LegalPage'
import { LoginPage } from './pages/auth/LoginPage'
import { SignupPage } from './pages/auth/SignupPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage'

export interface RouteMeta {
  title: string
  description: string
  noindex?: boolean
  jsonLd?: Record<string, unknown>
}

/**
 * M00-11 — SEO metadata lives with the route table.
 *
 * The prerenderer reads this to write real <title>/<meta> tags into each HTML
 * file, so a crawler that does not execute JavaScript still sees them. Deriving
 * them in a client effect instead is the most common way a "SEO-indexable"
 * marketing site ships with one shared title.
 */
export const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: 'InsightAI — AI-generated market research with defensible provenance',
    description:
      'Turn a business question into a structured research design, run fieldwork, and get findings every claim of which traces to a source. Built for SMB and enterprise research teams.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'InsightAI',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'AI-orchestrated market research platform covering secondary research, quantitative fieldwork, qualitative sessions and descriptive tracking.',
    },
  },
  '/solutions': {
    title: 'Solutions — InsightAI',
    description:
      'Brand health tracking, concept testing, market entry sizing and category understanding, run end to end with research-ops oversight.',
  },
  '/methodology': {
    title: 'Methodology — InsightAI',
    description:
      'TOSCA problem structuring, MECE issue trees, measurement and scaling governance, and questionnaire design intelligence grounded in Tourangeau’s cognitive model.',
  },
  '/pricing': {
    title: 'Pricing — InsightAI',
    description:
      'Transparent per-study pricing with min–max cost ranges before you commit. Self-serve for SMB, managed programmes for enterprise.',
  },
  '/resources': {
    title: 'Resources — InsightAI',
    description: 'Guides, methodology notes and worked examples from the InsightAI research team.',
  },
  '/legal/privacy': {
    title: 'Privacy Policy — InsightAI',
    description: 'How InsightAI collects, processes and retains personal data.',
  },
  '/legal/terms': {
    title: 'Terms of Service — InsightAI',
    description: 'The terms governing use of the InsightAI platform.',
  },
  '/auth/login': {
    title: 'Sign in — InsightAI',
    description: 'Sign in to your InsightAI research workspace.',
    noindex: true,
  },
  '/auth/signup': {
    title: 'Create your workspace — InsightAI',
    description: 'Start with a free brief. No methodology expertise required.',
    noindex: true,
  },
  '/auth/forgot-password': {
    title: 'Reset your password — InsightAI',
    description: 'Request a password reset link.',
    noindex: true,
  },
  '/auth/reset-password': {
    title: 'Choose a new password — InsightAI',
    description: 'Set a new password for your InsightAI account.',
    noindex: true,
  },
  '/auth/verify-email': {
    title: 'Confirm your email — InsightAI',
    description: 'Confirm your email address to finish setting up your workspace.',
    noindex: true,
  },
}

export interface AppRoute {
  path: string
  element: ReactElement
}

export const routes: AppRoute[] = [
  { path: '/', element: <HomePage /> },
  { path: '/solutions', element: <SolutionsPage /> },
  { path: '/methodology', element: <MethodologyPage /> },
  { path: '/pricing', element: <PricingPage /> },
  { path: '/resources', element: <ResourcesPage /> },
  { path: '/legal/privacy', element: <LegalPage document="privacy" /> },
  { path: '/legal/terms', element: <LegalPage document="terms" /> },
  { path: '/auth/login', element: <LoginPage /> },
  { path: '/auth/signup', element: <SignupPage /> },
  { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/auth/reset-password', element: <ResetPasswordPage /> },
  { path: '/auth/verify-email', element: <VerifyEmailPage /> },
]
